import { computed, onScopeDispose, reactive, toRaw, watch } from 'vue';
import { useRoute, useRouter, type LocationQuery } from 'vue-router';
import debounce from 'lodash/debounce';
import type { ReleaseSearchFilter, ReleaseSearchLibrary, ReleaseSpecificationType } from '@/shared/helpers/api';
import type { ReleaseState } from '@/shared/helpers/api/types/entities.ts';

export const ALL_RELEASE_STATES: ReleaseState[] = [
    'Planned',
    'Creating',
    'Open',
    'OnHold',
    'Closed',
    'Verifying',
    'Verified',
    'Approved',
    'Committing',
    'Committed',
    'Publishing',
    'Published',
    'Discarded'
];

/**
 * The specification type names come from the release specification classes and
 * are too technical for a filter list, so they are presented under short names.
 * Ordered by the label the user sees rather than by the underlying type name.
 */
export const SPECIFICATION_TYPE_LABELS: Record<ReleaseSpecificationType, string> = {
    ReleaseChain: 'Chain',
    ChangeDocument: 'Document',
    FilteredCodeSetReleaseSpecification: 'Filtered',
    CompositeChangeDocument: 'Multi document',
    PublishFilter: 'Publishing',
    ScopeCodeSetRelease: 'Scope'
};

export const ALL_SPECIFICATION_TYPES: ReleaseSpecificationType[] = [
    'ReleaseChain',
    'ChangeDocument',
    'FilteredCodeSetReleaseSpecification',
    'CompositeChangeDocument',
    'PublishFilter',
    'ScopeCodeSetRelease'
];

export function specificationTypeLabel(type: string | null): string {
    if (type === null) return 'Unknown';
    return SPECIFICATION_TYPE_LABELS[type as ReleaseSpecificationType] ?? type;
}

export type ReleaseStatusGroupKey = 'NeedsAttention' | 'InProgress' | 'Published' | 'Discarded';

/**
 * The release states are an implementation detail of the release workflow, so
 * they are presented as four groups: the ones waiting for somebody to act, the
 * ones the workflow moves along on its own, the ones that made it out, and the
 * ones that were abandoned.
 *
 * Open and Verified are the states the workflow cannot leave by itself; see the
 * transitions marked "Manual step" in ReleaseWorkflow.
 *
 * The Equinor palette has no vivid green: Bulma's `success` is mapped to a pale
 * blue, so `info` (Norwegian Woods) is the brand's green for a positive outcome.
 * See css/_bulma.scss.
 */
const STATES_NEEDING_ATTENTION: ReleaseState[] = ['Open', 'Verified'];

export const RELEASE_STATUS_GROUPS: {
    key: ReleaseStatusGroupKey;
    label: string;
    icon: string;
    colorClass: string;
    states: ReleaseState[];
}[] = [
    {
        key: 'NeedsAttention',
        label: 'Needs attention',
        icon: 'alert-circle',
        colorClass: 'has-text-warning',
        states: STATES_NEEDING_ATTENTION
    },
    {
        // OnHold belongs here rather than above: it waits for the release chain
        // that owns it, not for a person.
        key: 'InProgress',
        label: 'In progress',
        icon: 'cog',
        colorClass: 'has-text-grey',
        states: ALL_RELEASE_STATES.filter(s =>
            s !== 'Published' && s !== 'Discarded' && !STATES_NEEDING_ATTENTION.includes(s))
    },
    {
        key: 'Published',
        label: 'Published',
        icon: 'check-circle',
        colorClass: 'has-text-info',
        states: ['Published']
    },
    {
        key: 'Discarded',
        label: 'Discarded',
        icon: 'close-circle',
        colorClass: 'has-text-danger',
        states: ['Discarded']
    }
];

/** Presentation for a single state, derived from the group it belongs to. */
export function statusGroupForState(state: ReleaseState) {
    return RELEASE_STATUS_GROUPS.find(g => g.states.includes(state)) ?? RELEASE_STATUS_GROUPS[0];
}

/** The label a status group is shown under, for the active filter chips. */
export function statusGroupLabel(key: ReleaseStatusGroupKey): string {
    return RELEASE_STATUS_GROUPS.find(g => g.key === key)?.label ?? key;
}

/**
 * Which libraries to offer in the library filter, given the groups the user has
 * narrowed to. Selecting no group means no narrowing, and matching any one of
 * the chosen groups is enough.
 *
 * Libraries that are already selected are always offered, so that narrowing to
 * a group can never hide a selection the user still has to undo.
 */
export function narrowLibrariesByGroup(
    libraries: readonly ReleaseSearchLibrary[],
    groups: readonly string[],
    selected: readonly string[]
): string[] {
    if (groups.length === 0) return libraries.map(l => l.name);
    return libraries
        .filter(l => selected.includes(l.name) || l.groups.some(g => groups.includes(g)))
        .map(l => l.name);
}

/**
 * Opens on everything that is still moving plus the published releases, which
 * together leave only the discarded ones hidden.
 */
const DEFAULT_STATUS_GROUPS: ReleaseStatusGroupKey[] = ['NeedsAttention', 'InProgress', 'Published'];

export const DEFAULT_TAKE = 2000;

export const DATE_PRESETS = [
    { key: 'any', label: 'Any time', days: null },
    { key: '7d', label: 'Last 7 days', days: 7 },
    { key: '30d', label: 'Last 30 days', days: 30 },
    { key: '90d', label: 'Last 90 days', days: 90 },
    { key: '1y', label: 'Last year', days: 365 },
    { key: 'custom', label: 'Custom range', days: null }
] as const;

export type DatePresetKey = typeof DATE_PRESETS[number]['key'];

export type ReleaseSearchFilterState = {
    statusGroups: ReleaseStatusGroupKey[];
    specificationTypes: ReleaseSpecificationType[];
    libraries: string[];
    scopes: string[];
    datePreset: DatePresetKey;
    createdFrom: string | null;
    createdTo: string | null;
    onlyMine: boolean;
    createdByContains: string;
    take: number;
};

export type ActiveFilterChip = {
    key: string;
    label: string;
    remove: () => void;
};

export type BuiltReleaseSearchFilter = ReleaseSearchFilter & {
    states: ReleaseState[];
    specificationTypes: ReleaseSpecificationType[];
    libraries: string[];
    scopes: string[];
    createdFrom: string | null;
    createdTo: string | null;
    onlyMine: boolean;
    createdByContains: string | null;
    take: number;
};

function defaultFilters(): ReleaseSearchFilterState {
    return {
        statusGroups: [...DEFAULT_STATUS_GROUPS],
        specificationTypes: [],
        libraries: [],
        scopes: [],
        datePreset: 'any',
        createdFrom: null,
        createdTo: null,
        onlyMine: false,
        createdByContains: '',
        take: DEFAULT_TAKE
    };
}

function readArray(query: LocationQuery, key: string): string[] {
    const raw = query[key];
    if (raw === null || raw === undefined) return [];
    const values = Array.isArray(raw) ? raw : [raw];
    return values.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function readString(query: LocationQuery, key: string): string | null {
    const raw = query[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The previous release list encoded states as a JSON array in a `state`
 * parameter. Existing bookmarks and shared links still use it, so it is read as
 * a fallback and mapped onto the groups those states now belong to.
 */
function readLegacyStatusGroups(query: LocationQuery): ReleaseStatusGroupKey[] | null {
    const raw = readString(query, 'state');
    if (raw === null) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        const groups = parsed
            .filter((s): s is ReleaseState => ALL_RELEASE_STATES.includes(s as ReleaseState))
            .map(s => statusGroupForState(s).key);
        return [...new Set(groups)];
    } catch {
        return null;
    }
}

/**
 * Reads the filter state from a URL query. Exported for testing.
 */
export function readFiltersFromQuery(query: LocationQuery): ReleaseSearchFilterState {
    const filters = defaultFilters();

    const statusGroups = readArray(query, 'status')
        .filter((s): s is ReleaseStatusGroupKey => RELEASE_STATUS_GROUPS.some(g => g.key === s));
    const legacyGroups = readLegacyStatusGroups(query);
    if (statusGroups.length > 0) {
        filters.statusGroups = statusGroups;
    } else if (legacyGroups !== null) {
        filters.statusGroups = legacyGroups;
    } else if ('status' in query) {
        // Explicitly cleared by the user rather than absent.
        filters.statusGroups = [];
    }

    filters.specificationTypes = readArray(query, 'types')
        .filter((t): t is ReleaseSpecificationType => ALL_SPECIFICATION_TYPES.includes(t as ReleaseSpecificationType));
    filters.libraries = readArray(query, 'libraries');
    filters.scopes = readArray(query, 'scopes');

    const preset = readString(query, 'period');
    if (preset !== null && DATE_PRESETS.some(p => p.key === preset)) {
        filters.datePreset = preset as DatePresetKey;
    }
    filters.createdFrom = readString(query, 'from');
    filters.createdTo = readString(query, 'to');
    if (filters.datePreset === 'any' && (filters.createdFrom !== null || filters.createdTo !== null)) {
        filters.datePreset = 'custom';
    }

    filters.onlyMine = readString(query, 'mine') === 'true';
    // "Only my releases" supersedes the creator text, so a URL carrying both is
    // normalized rather than searching for a creator the panel cannot show.
    filters.createdByContains = filters.onlyMine ? '' : readString(query, 'createdBy') ?? '';

    const take = Number(readString(query, 'take'));
    if (Number.isFinite(take) && take > 0) {
        filters.take = take;
    }

    return filters;
}

/**
 * Only non-default values are written, so that a freshly opened page keeps a
 * clean URL and a shared link carries exactly the filters that were changed.
 * Exported for testing.
 */
export function buildQuery(filters: ReleaseSearchFilterState): LocationQuery {
    const query: LocationQuery = {};

    const groupsAreDefault = filters.statusGroups.length === DEFAULT_STATUS_GROUPS.length
        && DEFAULT_STATUS_GROUPS.every(g => filters.statusGroups.includes(g));
    if (!groupsAreDefault) {
        // An empty array must still be written, otherwise it would be read back
        // as "not specified" and silently fall back to the defaults.
        query.status = filters.statusGroups.length > 0 ? [...filters.statusGroups] : [''];
    }

    if (filters.specificationTypes.length > 0) query.types = [...filters.specificationTypes];
    if (filters.libraries.length > 0) query.libraries = [...filters.libraries];
    if (filters.scopes.length > 0) query.scopes = [...filters.scopes];
    if (filters.datePreset !== 'any') query.period = filters.datePreset;
    if (filters.datePreset === 'custom') {
        if (filters.createdFrom) query.from = filters.createdFrom;
        if (filters.createdTo) query.to = filters.createdTo;
    }
    if (filters.onlyMine) query.mine = 'true';
    if (filters.createdByContains) query.createdBy = filters.createdByContains;
    if (filters.take !== DEFAULT_TAKE) query.take = String(filters.take);

    return query;
}

function isoDaysAgo(days: number): string {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - days);
    return date.toISOString().slice(0, 10);
}

/**
 * The date inputs yield a plain date, but the API compares against a point in
 * time. Without this the last day of a custom range would be excluded, because
 * the bare date binds to midnight at the start of that day.
 */
function endOfDay(date: string): string {
    return `${date}T23:59:59.999`;
}

/** Compares two filter states; both are built by the same functions, so their key order matches. */
function sameFilters(a: ReleaseSearchFilterState, b: ReleaseSearchFilterState): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The filter as the API expects it, with the date preset resolved to a date
 * and the status groups expanded to the release states they cover.
 * Exported for testing.
 */
export function buildApiFilter(filters: ReleaseSearchFilterState): BuiltReleaseSearchFilter {
    const preset = DATE_PRESETS.find(p => p.key === filters.datePreset);
    const createdFrom = typeof preset?.days === 'number'
        ? isoDaysAgo(preset.days)
        : filters.datePreset === 'custom' ? filters.createdFrom : null;
    const createdTo = filters.datePreset === 'custom' && filters.createdTo !== null
        ? endOfDay(filters.createdTo)
        : null;

    const states = RELEASE_STATUS_GROUPS
        .filter(g => filters.statusGroups.includes(g.key))
        .flatMap(g => g.states);

    return {
        states,
        specificationTypes: [...filters.specificationTypes],
        libraries: [...filters.libraries],
        scopes: [...filters.scopes],
        createdFrom,
        createdTo,
        onlyMine: filters.onlyMine,
        // "Only my releases" wins: the panel disables the creator field while it is
        // on, so sending a leftover value would narrow the results invisibly.
        createdByContains: filters.onlyMine ? null : filters.createdByContains.trim() || null,
        take: filters.take
    };
}

export function useReleaseSearchFilters() {
    const route = useRoute();
    const router = useRouter();

    const filters = reactive<ReleaseSearchFilterState>(readFiltersFromQuery(route.query));

    // The path is captured when the change happens rather than when the write is
    // flushed, so a navigation during the debounce window cannot rewrite the
    // query string of whichever page was opened next.
    const writeQuery = debounce((path: string) => {
        // Comparing filter states rather than query objects, because a
        // single-valued array is read back from the URL as a plain string.
        if (sameFilters(readFiltersFromQuery(route.query), toRaw(filters))) return;
        void router.replace({ path, query: buildQuery(filters) });
    }, 300);

    onScopeDispose(() => writeQuery.cancel());

    watch(filters, () => writeQuery(route.path), { deep: true });

    // The creator field is disabled while "Only my releases" is on. Clearing it
    // keeps the panel, the URL and the query in agreement; leaving it would go on
    // narrowing the results by a value the user can no longer see or edit.
    watch(() => filters.onlyMine, onlyMine => {
        if (onlyMine) filters.createdByContains = '';
    });

    // Keeps the panel in step with browser back and forward navigation.
    watch(() => route.query, query => {
        const incoming = readFiltersFromQuery(query);
        if (sameFilters(incoming, toRaw(filters))) return;
        Object.assign(filters, incoming);
    });

    const apiFilter = computed<ReleaseSearchFilter>(() => buildApiFilter(filters));

    const activeChips = computed<ActiveFilterChip[]>(() => {
        const chips: ActiveFilterChip[] = [];

        filters.statusGroups.forEach(group => chips.push({
            key: `status:${group}`,
            label: statusGroupLabel(group),
            remove: () => { filters.statusGroups = filters.statusGroups.filter(g => g !== group); }
        }));
        filters.specificationTypes.forEach(type => chips.push({
            key: `type:${type}`,
            label: specificationTypeLabel(type),
            remove: () => { filters.specificationTypes = filters.specificationTypes.filter(t => t !== type); }
        }));
        filters.libraries.forEach(library => chips.push({
            key: `library:${library}`,
            label: library,
            remove: () => { filters.libraries = filters.libraries.filter(l => l !== library); }
        }));
        filters.scopes.forEach(scope => chips.push({
            key: `scope:${scope}`,
            label: scope,
            remove: () => { filters.scopes = filters.scopes.filter(s => s !== scope); }
        }));

        if (filters.datePreset !== 'any') {
            const preset = DATE_PRESETS.find(p => p.key === filters.datePreset);
            const label = filters.datePreset === 'custom'
                ? `${filters.createdFrom ?? '...'} to ${filters.createdTo ?? '...'}`
                : preset?.label ?? filters.datePreset;
            chips.push({
                key: 'period',
                label,
                remove: () => {
                    filters.datePreset = 'any';
                    filters.createdFrom = null;
                    filters.createdTo = null;
                }
            });
        }

        if (filters.onlyMine) {
            chips.push({ key: 'mine', label: 'Only my releases', remove: () => { filters.onlyMine = false; } });
        }
        if (filters.createdByContains) {
            chips.push({
                key: 'createdBy',
                label: `Created by contains "${filters.createdByContains}"`,
                remove: () => { filters.createdByContains = ''; }
            });
        }

        return chips;
    });

    function clearAll(): void {
        Object.assign(filters, defaultFilters(), { statusGroups: [] });
    }

    function resetToDefaults(): void {
        Object.assign(filters, defaultFilters());
    }

    return { filters, apiFilter, activeChips, clearAll, resetToDefaults };
}
